<script>
import Layout from '@layouts/adminLayout.vue'
import FormItemGroup from '@components/form-item-group.vue'
import FormItem from '@components/form-item.vue'
import TagSelector from '@components/tag-selector.vue'
import SearchRecipientResult from '@components/network/search-recipient-result.vue'
import LoaderIcon from '@components/loader-icon.vue'
import MottoIcon from '@components/motto-icon.vue'
import MottoBreadcrumbs from '@components/motto-breadcrumbs.vue'
import GrayHeader from '@components/gray-header.vue'
import Tooltip from '@components/tooltip.vue'
import _ from 'lodash'
import {
  getMySentReferral,
  getMyOfficeSentReferral,
  acceptMySentReferral,
  expireMySentReferral,
} from '@src/api/networkConnect'
import moment from 'moment'
import router from '@router'
import { parseErrorMessage } from '@utils/string-helpers'
import { authComputed } from '@state/helpers'
import { referralStatusTooltips } from '@utils/consts/referralStatusTooltips'
import { transformMany as transformRecipients } from '@src/api/transformers/recipientTransformer'
import { saveDataTracking } from '@src/api/tracking'

export default {
  page: {
    title: 'Network Connect Referral',
    meta: [
      {
        name: 'description',
        content: 'The Network Connect Referral page.',
      },
    ],
  },
  components: {
    Layout,
    FormItem,
    FormItemGroup,
    MottoBreadcrumbs,
    SearchRecipientResult,
    TagSelector,
    GrayHeader,
    Tooltip,
    MottoIcon,
    LoaderIcon,
  },
  data() {
    return {
      loading: true,
      error: false,
      disableAcceptanceButtons: false,
      referral: {},
      referralCategory: null,
      isOfficeReferral: false,
    }
  },
  computed: {
    ...authComputed,
    primaryOfficeTradeName() {
      return this.currentUser.primaryOffice?.tradeName || ''
    },
    referralIdentifier() {
      if (this.referralCategory === 'received') {
        return `Received ${moment.unix(this.referral.receivedAt).format('M/D/yyyy h:mm a').toUpperCase()}`
      }

      if (this.referral.borrowerFirstName && this.referral.borrowerLastName) {
        return `${this.referral.borrowerFirstName} ${this.referral.borrowerLastName}`
      }

      if (this.referral.borrowerLastName) {
        return `${this.referral.borrowerLastName}`
      }

      if (this.referral.borrowerFirstName) {
        return `${this.referral.borrowerFirstName}`
      }

      return this.referral.state
    },
    referralRecipients() {
      if (this.referral.referralType === 'State-wide') {
        return _.orderBy(this.referralAcceptedRecipients, ['recipientResponseOrder', 'lastName']) || []
      }

      return _.orderBy(this.referral.recipients, ['recipientResponseOrder', 'lastName']) || []
    },
    referralAcceptedRecipients() {
      return this.referral.recipients.filter((recipient) => recipient.receivedStatus === 'Pending Sender')
    },
    referralAcceptedRecipientsIds() {
      return this.referralAcceptedRecipients.map((recipient) => recipient.id) || []
    },
    amountOfRecipientsSentence() {
      if (!this.referralRecipients?.length) {
        return ''
      }

      if (this.referral.status === 'Completed' && this.referralRecipients.length === 1) {
        return 'Original Recipient'
      }

      if (this.referral.status === 'Completed') {
        return 'Original Recipients'
      }

      if (this.referralRecipients.length === 1) {
        return `1 recipient`
      }

      return `${this.referralRecipients.length} recipients`
    },
    getRecipientType() {
      if (this.referral.referralType === 'Users' || this.referral.referralType === 'State-wide') {
        return 'user'
      }

      if (
        this.referral.referralType === 'Offices' &&
        (this.referral.status === 'Pending Confirmation' || this.referral.status === 'Completed')
      ) {
        return 'user'
      }

      return 'office'
    },
    isUserSender() {
      return this.currentUser.id === this.referral.createdBy.id
    },
  },
  created() {
    if (!this.canManageNetworkConnect) {
      router.push({ name: 'home' })
    }
    this.loadSentReferral()
  },
  methods: {
    async loadSentReferral({ isOfficeReferral = false } = {}) {
      try {
        this.loading = true
        const { data: referral } = isOfficeReferral
          ? await getMyOfficeSentReferral({ id: this.$route.params.id })
          : await getMySentReferral({ id: this.$route.params.id })
        this.isOfficeReferral = isOfficeReferral
        this.referral = referral
        this.referralCategory = 'sent'
        this.referral.sentDateStr = moment.unix(this.referral.sentDate).format('M/D/yyyy')
        this.referral.recipients =
          this.getRecipientType === 'user' ? transformRecipients(this.referral.users, referral) : this.referral.offices
        this.loading = false
      } catch (err) {
        if (!isOfficeReferral) {
          await this.loadSentReferral({ isOfficeReferral: true })
          return
        }
        this.error = true
        this.loading = false
      }
    },

    getAcceptedUserForCompletedReferral(referral) {
      if (referral.completedUser) {
        return referral.completedUser
      }

      return referral.users ? referral.users[0] : {}
    },

    getStatusTooltipText(status) {
      return referralStatusTooltips({
        status,
        tradeName: this.primaryOfficeTradeName,
      })
    },
    async expireReferral() {
      try {
        this.disableAcceptanceButtons = true
        await this.$bvModal
          .msgBoxConfirm(`Are you sure you want to expire this referral?`, {
            modalClass: 'confirmation-modal',
            title: 'Confirm',
            okTitle: 'Accept',
            centered: true,
          })
          .then(async (confirmation) => {
            if (confirmation) {
              await expireMySentReferral({ id: this.referral.id })
              this.$root.$bvToast.toast(`Referral expired successfully.`, {
                toaster: 'b-toaster-bottom-center',
                toastClass: 'toast-success',
              })

              await router.push({
                name: 'network-connect',
                params: {
                  selectedTab: 'sent',
                },
              })
            }
          })
      } catch (err) {
        this.$bvToast.toast(parseErrorMessage(err), {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-error',
        })
      } finally {
        this.disableAcceptanceButtons = false
      }
    },
    resendReferral() {
      this.$router.push({
        name: 'network-connect-resend-referral',
        params: {
          id: this.referral.id,
        },
      })
    },
    async acceptReferralRecipient({ id }) {
      try {
        this.disableAcceptanceButtons = true
        await this.$bvModal
          .msgBoxConfirm(
            `Are you sure you want to select this MLO?  This will complete the referral and display the MLOs Work line and Motto Email to you, and your contact information to them.`,
            {
              modalClass: 'confirmation-modal',
              title: 'Confirm',
              okTitle: 'Accept',
              centered: true,
            }
          )
          .then(async (confirmation) => {
            if (confirmation) {
              await acceptMySentReferral({ id: this.referral.id }, id)
              await saveDataTracking('REFERRAL - COMPLETED')
              await this.showCompleteReferralToastAndRedirect()
            }
          })
      } catch (err) {
        this.$bvToast.toast(parseErrorMessage(err), {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-error',
        })
      } finally {
        this.disableAcceptanceButtons = false
      }
    },

    async showCompleteReferralToastAndRedirect() {
      const h = this.$createElement
      const vNodesMsg = h('h4', { class: ['color-white', 'mb-0'], style: { lineHeight: '25px' } }, [
        `The referral has been completed.  You may now contact the Recipient using their Motto Email`,
        h('MottoIcon', {
          props: {
            name: 'Envelope',
            'icon-color': 'orange',
          },
          class: ['mx-2'],
        }),
        `or Work Line`,
        h('MottoIcon', {
          props: {
            name: 'Phone',
            'icon-color': 'orange',
          },
          class: ['mx-2'],
        }),
      ])
      this.$root.$bvToast.toast([vNodesMsg], {
        toaster: 'b-toaster-bottom-center',
        toastClass: 'toast-primary-blue',
        autoHideDelay: 10000,
      })

      await router.push({
        name: 'network-connect',
        params: {
          selectedTab: 'sent',
        },
      })
    },
  },
}
</script>

<template>
  <Layout>
    <LoaderIcon
      v-if="loading || error"
      :error="error"
      error-message="This referral is no longer available, either because it expired or another MLO was selected.  It has been removed from Network Connect."
      class="m-auto"></LoaderIcon>
    <div v-else>
      <GrayHeader>
        <MottoBreadcrumbs
          :items="[
            {
              label: 'Network Connect',
              routeName: 'network-connect',
              routeParams: { selectedTab: isOfficeReferral ? 'office_sent' : 'sent' },
            },
            {
              label: `Referral - ${referralIdentifier}`,
            },
          ]"
          :back-route="'network-connect'"
          :back-route-params="{ selectedTab: isOfficeReferral ? 'office_sent' : 'sent' }" />
        <BaseTitle class="gray-header-title">Referral - {{ referralIdentifier }}</BaseTitle>
        <p class="d-block d-lg-none mt-3 color-secondary-light-blue">{{ getStatusTooltipText(referral.status) }}</p>
      </GrayHeader>
      <FormItemGroup>
        <FormItem label="Borrower First Name" :value="referral.borrowerFirstName"></FormItem>
        <FormItem label="Borrower Last Name" :value="referral.borrowerLastName"></FormItem>
        <div></div>
      </FormItemGroup>
      <FormItemGroup>
        <FormItem label="Subject Property Address State" :value="referral.state"></FormItem>
        <FormItem label="Loan Type" :value="referral.loanType"></FormItem>
        <FormItem label="Loan Amount" :value="referral.loanAmount"></FormItem>
      </FormItemGroup>
      <FormItemGroup v-if="!isUserSender">
        <FormItem label="Sender Name" :value="referral.createdBy.name"></FormItem>
        <FormItem label="Sender Email" :value="referral.createdBy.email"></FormItem>
        <FormItem label="Sender Phone" :value="referral.createdBy.phone"></FormItem>
      </FormItemGroup>
      <FormItemGroup>
        <FormItem label="Date Sent" :value="referral.sentDateStr"></FormItem>
        <div class="position-relative d-flex">
          <FormItem label="Status" :value="referral.status"></FormItem>
          <Tooltip
            v-if="getStatusTooltipText(referral.status)"
            :target="`${referral.id}`"
            placement="top"
            theme="blue"
            :class="$style.statusTooltip">
            {{ getStatusTooltipText(referral.status) }}
          </Tooltip>
        </div>
        <FormItem
          v-if="referral.referralType === 'State-wide'"
          label="Recipients"
          :value="referral.referralType"></FormItem>
        <div v-else></div>
      </FormItemGroup>
      <FormItemGroup v-if="referral.status === 'Completed'">
        <FormItem label="Recipient Name" :value="getAcceptedUserForCompletedReferral(referral).name"></FormItem>
        <FormItem label="Recipient Work line" :value="getAcceptedUserForCompletedReferral(referral).phone">
          <template v-slot:afterValue>
            <a
              v-if="referral.status === 'Completed' && getAcceptedUserForCompletedReferral(referral).phone"
              :href="`tel:${getAcceptedUserForCompletedReferral(referral).phone || ''}`"
              :class="$style.contactIcon">
              <MottoIcon :id="`phone${referral.id}`" name="Phone" icon-color="orange" class="ml-2"></MottoIcon>
            </a>
            <b-tooltip :target="`phone${referral.id}`" :placement="`top`" custom-class="tooltipBlue">
              Call referree’s work line
            </b-tooltip>
          </template>
        </FormItem>
        <FormItem label="Recipient Email" :value="getAcceptedUserForCompletedReferral(referral).email">
          <template v-slot:afterValue>
            <a
              v-if="referral.status === 'Completed' && getAcceptedUserForCompletedReferral(referral).email"
              :href="`mailto:${getAcceptedUserForCompletedReferral(referral).email}?subject=Completed ${
                referral.state || ''
              } ${referral.loanType || ''} deal level referral`"
              :class="$style.contactIcon">
              <MottoIcon :id="`envelope${referral.id}`" name="Envelope" icon-color="orange" class="ml-2"></MottoIcon>
              <b-tooltip :target="`envelope${referral.id}`" :placement="`top`" custom-class="tooltipBlue">
                Email referree
              </b-tooltip>
            </a>
          </template>
        </FormItem>
      </FormItemGroup>
      <template
        v-if="
          (referral.referralType !== 'State-wide' || referralAcceptedRecipients.length > 0) &&
          referral.recipients.length > 0
        ">
        <div class="position-relative">
          <p class="p1 mt-10 mb-10 color-dark-gray">{{ amountOfRecipientsSentence }}:</p>
          <p v-if="getRecipientType === 'user'" :class="$style.recipientResponseText" class="p1 color-dark-gray">
            Recipient Response:
          </p>

          <div :class="$style.searchResultsContainer">
            <SearchRecipientResult
              v-for="recipient in referralRecipients.map((referralRecipient) => ({
                ...referralRecipient,
                recipientType: getRecipientType,
              }))"
              :key="recipient.id"
              :recipient="recipient"
              disabled>
              <template v-if="getRecipientType === 'user'" v-slot:actionButton>
                <div :class="$style.selectButtonContainer">
                  <p class="text-align-left p1 color-dark-gray">
                    {{ recipient.recipientResponse }}
                  </p>
                  <BaseButton
                    v-if="referral.status !== 'Completed'"
                    :options="{ defaultPadding: true }"
                    theme="green"
                    :disabled="
                      disableAcceptanceButtons ||
                      !(isUserSender && referralAcceptedRecipientsIds.includes(recipient.id))
                    "
                    @click="acceptReferralRecipient(recipient)">
                    Select
                  </BaseButton>
                </div>
              </template>
            </SearchRecipientResult>
          </div>
        </div>
        <div :class="$style.selectedRecipientsTags">
          <TagSelector v-for="recipient in referralRecipients" :key="recipient.id" :removable="false">
            {{ recipient.name || recipient.tradeName }}
          </TagSelector>
        </div>
      </template>

      <div
        v-if="isUserSender && (referral.status === 'Expired' || referral.status === 'Declined')"
        :class="$style.bottomButtonsContainer">
        <BaseButton :options="{ defaultPadding: true }" @click="resendReferral">Resend Referral</BaseButton>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.selectButtonContainer {
  @media (max-width: $lg-breakpoint) {
    width: 205px;
    min-width: 205px;
    margin: 0 10px 5px 10px;

    button {
      margin: 0 !important;
    }
  }

  @media (max-width: $md-breakpoint) {
    width: 100%;
  }

  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 205px;
  min-width: 205px;

  button {
    margin: 0 1rem 0 0;
  }
}

.statusTooltip {
  display: flex;
  flex-direction: column-reverse;
  margin-bottom: 5px;
  margin-left: 10px;

  > div {
    position: relative;
    top: unset;
    right: unset;
  }
}

.selectedRecipientsTags {
  display: flex;
  flex-wrap: wrap;
  min-height: 43px;

  > div {
    margin-right: 6px;
    margin-bottom: 6px;
  }
}

.searchResultsContainer {
  max-height: 500px;
  padding: 10px;
  margin-bottom: 20px;
  overflow-y: scroll;
  border: 1px solid $gray;
  border-radius: 8px;

  > div {
    margin-bottom: 10px;

    &:last-child {
      margin-bottom: 0;
    }

    > div:first-child {
      max-width: 65%;
      @media (max-width: $md-breakpoint) {
        width: 100%;
        max-width: 100%;
      }
    }
  }
}

.bottomButtonsContainer {
  @media (max-width: $lg-breakpoint) {
    margin-top: 32px;
  }

  display: flex;
  justify-content: center;
  margin-top: 40px;

  button:first-child {
    margin-right: 24px;
  }
}

.contactIcon {
  color: unset;
}

.recipientResponseText {
  @media (max-width: $md-breakpoint) {
    display: none;
  }

  @media (max-width: $lg-breakpoint) {
    right: 155px;
  }

  position: absolute;
  top: 0;
  right: 145px;
}
</style>
